import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { FieldError } from '@/components/feedback/FieldError'
import { useToastStack } from '@/components/feedback/toast-stack'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { formatDateOnly, todayAsDateInput } from '@/lib/format'
import { fieldErrorProps } from '@/lib/form/field-error'
import { assignValues, useRecordEditor } from '@/lib/form/use-record-editor'
import { cubeWallClock, istanbulToIso, normalizeTime } from '@/lib/hr'
import {
  descriptionFormSchema,
  fieldMessages,
  formatLeaveRange,
  leaveFormSchema,
  leaveFormValue,
  leaveServerFields,
  leaveValues,
  MOVEMENT_SERVER_FIELDS,
  movementFormSchema,
  sameLeave,
  type DescriptionValue,
  type GirisCikis,
  type LeaveFormValue,
  type LeaveValues,
  type MovementValue,
} from '@/features/hr/request-value'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { HR_VIEWS, hrFilters, hrRowsQuery, mapHrRows, type PersonelRow } from '@/services/cube/hr'
import { hrApi, type HrRequestCreate, type HrRequestDetail } from '@/services/http/hr'
import { refreshRequestViews, trackRequestResult, useMovementCheck } from '@/features/hr/hr-actions'
import { LeaveFields, MovementFields, SubmitConfirm } from '@/features/hr/request-fields'
import { targetCopy } from '@/features/hr/request-copy'
import { useAuthStore } from '@/stores/auth-store'

const t = messages.hr
const r = t.approvals

export interface RequestFormTarget {
  kind: HrRequestCreate['kind']
  sicilno?: string
  name?: string | null
  hareketId?: string
  girisCikis?: GirisCikis
  /** Düzenlenen hareketin Cube zamanı (İstanbul duvar saati). */
  wallClock?: string | null
  /** İzin güncelleme: Passgage izni ve şimdiki hali. */
  leaveId?: string
  leave?: LeaveValues
}

/** Personel arama: kapsamdaki kartlar (Cube firma kısıtı geçerli), ad ya da sicil ile. */
function PersonPicker({
  selected,
  onSelect,
}: {
  selected: { sicilno: string; name: string | null } | null
  onSelect: (person: { sicilno: string; name: string | null }) => void
}) {
  const [term, setTerm] = useState('')
  const search = useDebouncedValue(term.trim(), 250)
  const view = HR_VIEWS.personel
  const members = ['personelKey', 'sicilno', 'adisoyadi', 'firma', 'sube'] as const
  const filters = hrFilters({ view, equals: { diaDurum: ['A'] }, search })
  const query = useCubeLiveQuery({
    queryKey: queryKeys.cube(view, { kind: 'picker', search }),
    query: hrRowsQuery({
      view,
      members,
      filters,
      order: [{ id: 'adisoyadi', desc: false }],
      rowKey: 'personelKey',
      limit: 8,
      offset: 0,
    }),
    select: mapHrRows<Pick<PersonelRow, (typeof members)[number]>>(view, members),
    enabled: search.length >= 2,
    subscribe: false,
  })
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="hr-request-person">{r.person}</Label>
      {selected && (
        <span className="flex items-center gap-2 text-sm">
          <Avatar name={selected.name ?? '?'} size="sm" />
          {selected.name} · {selected.sicilno}
        </span>
      )}
      <Input
        id="hr-request-person"
        value={term}
        placeholder={r.personPlaceholder}
        onChange={(event) => setTerm(event.target.value)}
      />
      {search.length >= 2 && (
        <ul className="border-border rounded-control flex max-h-56 flex-col overflow-y-auto border">
          {(query.data ?? []).map((person) => (
            <li key={person.personelKey}>
              <button
                type="button"
                className="hover:bg-surface-muted flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                onClick={() => {
                  onSelect({ sicilno: person.sicilno, name: person.adisoyadi })
                  setTerm('')
                }}
              >
                <Avatar name={person.adisoyadi ?? '?'} size="sm" />
                <span className="flex flex-col">
                  <span>{person.adisoyadi}</span>
                  <span className="text-text-muted text-xs">
                    {person.sicilno} · {person.firma} · {person.sube}
                  </span>
                </span>
              </button>
            </li>
          ))}
          {query.data?.length === 0 && (
            <li className="text-text-muted px-3 py-2 text-sm">{r.personEmpty}</li>
          )}
        </ul>
      )}
    </div>
  )
}

function initialValue(target: RequestFormTarget | null): MovementValue {
  const clock = cubeWallClock(target?.wallClock)
  return {
    girisCikis: target?.girisCikis ?? 'G',
    date: clock?.date ?? todayAsDateInput(),
    time: clock?.time ?? '',
    description: '',
  }
}

/** Kirli formun kapatma koruması (`useRecordEditor().requestClose`); Sheet'in kendi kapatması da ondan geçer. */
type CloseGuard = (close: () => void) => void
type RegisterGuard = (guard: CloseGuard | null) => void

/** Formun güncel kapatma korumasını Sheet'e bildirir. */
function useRegisterGuard(register: RegisterGuard | undefined, guard: CloseGuard) {
  useEffect(() => {
    register?.(guard)
  })
  useEffect(() => () => register?.(null), [register])
}

/** Mali işlerin talep formu: yeni elle giriş/çıkış ya da mevcut hareketin düzenlemesi. */
export function RequestFormSheet({
  target,
  onClose,
}: {
  target: RequestFormTarget | null
  onClose: () => void
}) {
  const guard = useRef<CloseGuard | null>(null)
  const register = useCallback<RegisterGuard>((next) => {
    guard.current = next
  }, [])
  return (
    <Sheet
      open={target !== null}
      onOpenChange={(open) => {
        if (open) return
        if (guard.current) guard.current(onClose)
        else onClose()
      }}
    >
      <SheetContent>
        {target && (
          <RequestForm
            key={`${target.kind}-${target.hareketId ?? target.leaveId ?? ''}`}
            target={target}
            onClose={onClose}
            register={register}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}

function RequestForm({
  target,
  onClose,
  register,
}: {
  target: RequestFormTarget
  onClose: () => void
  register: RegisterGuard
}) {
  if (target.kind === 'giris_cikis_sil') {
    return <MovementDeleteForm target={target} onClose={onClose} register={register} />
  }
  if (target.kind === 'izin_iptal' && target.leave) {
    return (
      <LeaveCancelForm
        target={target}
        current={target.leave}
        onClose={onClose}
        register={register}
      />
    )
  }
  if (target.kind === 'izin_guncelle' && target.leave) {
    return (
      <LeaveRequestForm
        target={target}
        current={target.leave}
        onClose={onClose}
        register={register}
      />
    )
  }
  return <MovementRequestForm target={target} onClose={onClose} register={register} />
}

/**
 * Farklı firmalarda aktif kartı olan personel: sunucu `firm_ambiguous` ile seçenekleri döner,
 * form firma seçtirir ve yeniden gönderir (gönderim de aynı kart kuralıyla çalışır).
 */
function useFirmChoice() {
  const [options, setOptions] = useState<string[] | null>(null)
  const [firma, setFirma] = useState('')
  return {
    /** Talep gövdesine eklenecek alan: yalnız firma seçildiyse `{ firma }`. */
    body: options && firma ? { firma } : {},
    ready: options === null || firma !== '',
    reset: () => {
      setOptions(null)
      setFirma('')
    },
    /** Hata firma belirsizliğiyse seçenekleri açar ve `true` döner (toast gösterilmez). */
    handle: (error: unknown) => {
      const problem = toProblem(error)
      if (problem.code !== 'firm_ambiguous') return false
      setOptions((problem.errors ?? []).map((item) => item.message))
      return true
    },
    field: options && (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="hr-request-firm">{r.firm}</Label>
        <OptionSelect
          id="hr-request-firm"
          className="w-64"
          value={firma}
          options={options.map((value) => ({ value, label: value }))}
          aria-label={r.firm}
          onValueChange={setFirma}
        />
        <span className="text-warning text-xs">{r.firmHint}</span>
      </div>
    ),
  }
}

/** Talep açıldı: bildirim, görünümleri tazeleme, onaysız işlenende sonucu izleme ve kapatma. */
function useRequestCreated(onClose: () => void) {
  const queryClient = useQueryClient()
  const toastApi = useToastStack()
  return (created: HrRequestDetail) => {
    toastApi.toast({
      type: 'success',
      title: created.status === 'onaylandi' ? targetCopy(created).approved : r.created,
    })
    refreshRequestViews(queryClient, created)
    // Onaysız işlenen talebin hedef sistem sonucu bildirimde izlenir.
    if (created.status === 'onaylandi') {
      trackRequestResult(created, toastApi, (latest) => refreshRequestViews(queryClient, latest))
    }
    onClose()
  }
}

/** Yalnız açıklama sorulan formun alanı: hata (doğrulama ya da sunucu) textarea'nın altında. */
function DescriptionField({
  id,
  placeholder,
  value,
  error,
  onChange,
}: {
  id: string
  placeholder: string
  value: string
  error?: string
  onChange: (next: string) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{r.descriptionLabel}</Label>
      <Textarea
        id={id}
        value={value}
        maxLength={2000}
        placeholder={placeholder}
        {...fieldErrorProps(id, error)}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldError id={id} message={error} />
    </div>
  )
}

/**
 * İzin iptali ve hareket silme: yalnız açıklama sorulur. Form, kaydetme akışı, kapatma koruması ve
 * onaysız işlemenin özet onayı burada; `build` talep gövdesini kurar.
 */
function useDescriptionRequest({
  onClose,
  register,
  build,
  onError,
}: {
  onClose: () => void
  register?: RegisterGuard
  build: (description: string, direct: boolean) => HrRequestCreate
  onError?: (error: unknown) => boolean
}) {
  const form = useForm<DescriptionValue>({
    resolver: zodResolver(descriptionFormSchema),
    defaultValues: { description: '' },
  })
  const description = useWatch({ control: form.control, name: 'description' })
  const canApprove = useAuthStore((state) => state.hasPermission('hr.request:approve'))
  const [direct, setDirect] = useState(canApprove)
  const [confirming, setConfirming] = useState(false)
  const created = useRequestCreated(onClose)
  const approving = canApprove && direct
  const editor = useRecordEditor({
    form,
    submit: (values) => hrApi.createRequest(build(values.description.trim(), approving)),
    onSaved: created,
    onError,
    failedTitle: r.actionFailed,
  })
  useRegisterGuard(register, editor.requestClose)
  /** Onaysız işlemede önce doğrula, sonra özet onayı aç; aksi hâlde doğrudan kaydet. */
  const primary = async () => {
    if (!approving) return editor.save()
    if (await form.trigger()) setConfirming(true)
  }
  return {
    editor,
    description,
    error: form.formState.errors.description?.message,
    setDescription: (next: string) => assignValues(form, { description: next }),
    canApprove,
    direct,
    approving,
    setDirect,
    confirming,
    setConfirming,
    primary,
  }
}

/** Onay izni olan, talebi onaya göndermeden doğrudan işleyebilir. */
function DirectToggle({
  direct,
  onChange,
  hint,
}: {
  direct: boolean
  onChange: (next: boolean) => void
  hint: string
}) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <Checkbox checked={direct} onCheckedChange={(next) => onChange(next === true)} />
      <span className="flex flex-col gap-0.5">
        <span>{r.direct}</span>
        <span className="text-text-muted text-xs">{hint}</span>
      </span>
    </label>
  )
}

function LeaveRequestForm({
  target,
  current,
  onClose,
  register,
}: {
  target: RequestFormTarget
  current: LeaveValues
  onClose: () => void
  register?: RegisterGuard
}) {
  const form = useForm<LeaveFormValue>({
    resolver: zodResolver(leaveFormSchema),
    defaultValues: leaveFormValue(current),
  })
  // Tüm alanların varsayılanı var; `useWatch` tipi yine de kısmidir.
  const value = useWatch({ control: form.control }) as LeaveFormValue
  const canApprove = useAuthStore((state) => state.hasPermission('hr.request:approve'))
  const [direct, setDirect] = useState(canApprove)
  const [confirming, setConfirming] = useState(false)
  const firm = useFirmChoice()
  const created = useRequestCreated(onClose)
  const body = (values: LeaveFormValue): HrRequestCreate => ({
    ...firm.body,
    kind: 'izin_guncelle',
    sicilno: target.sicilno ?? '',
    leave_id: target.leaveId ?? null,
    leave: leaveValues(values),
    description: values.description.trim(),
    direct: canApprove && direct,
  })
  const editor = useRecordEditor({
    form,
    submit: (values) => hrApi.createRequest(body(values)),
    onSaved: created,
    onError: firm.handle,
    serverFields: leaveServerFields(value.saatlik),
    failedTitle: r.actionFailed,
  })
  useRegisterGuard(register, editor.requestClose)
  const unchanged = sameLeave(leaveValues(value), current)
  /** Onaysız işlemede önce doğrula, sonra özet onayı aç; aksi hâlde doğrudan kaydet. */
  const primary = async () => {
    if (!(canApprove && direct)) return editor.save()
    if (await form.trigger()) setConfirming(true)
  }
  return (
    <>
      <SheetHeader>
        <SheetTitle>{r.leave.title}</SheetTitle>
        <SheetDescription>{r.leave.targetsNote}</SheetDescription>
      </SheetHeader>
      <SheetBody className="flex flex-col gap-4">
        <span className="flex items-center gap-2 text-sm">
          <Avatar name={target.name ?? '?'} size="sm" />
          {target.name} · {target.sicilno}
        </span>
        <LeaveFields
          idPrefix="hr-leave"
          value={value}
          current={current}
          errors={fieldMessages(form.formState.errors)}
          onChange={(next) => assignValues(form, next)}
        />
        {firm.field}
        {canApprove && (
          <DirectToggle direct={direct} onChange={setDirect} hint={r.leave.directHint} />
        )}
      </SheetBody>
      <SheetFooter>
        <Button variant="secondary" onClick={() => editor.requestClose(onClose)}>
          {t.actions.cancel}
        </Button>
        <Button disabled={unchanged || !firm.ready || editor.saving} onClick={() => void primary()}>
          {canApprove && direct ? r.leave.submitDirect : r.submit}
        </Button>
      </SheetFooter>
      <SubmitConfirm
        open={confirming}
        title={r.directConfirmTitle}
        body={r.leave.approveConfirmBody}
        lines={[
          [r.person, `${target.name ?? ''} · ${target.sicilno ?? ''}`],
          [r.leave.current, `${current.tur} · ${formatLeaveRange(current)}`],
          [r.leave.requested, `${value.tur} · ${formatLeaveRange(leaveValues(value))}`],
          [r.descriptionLabel, value.description.trim()],
        ]}
        pending={editor.saving}
        confirmLabel={r.leave.submitDirect}
        onOpenChange={setConfirming}
        onConfirm={() => void editor.save().finally(() => setConfirming(false))}
      />
      {editor.dialog}
    </>
  )
}

/** İzin iptal talebi: iznin mevcut hâli gösterilir, yalnız açıklama sorulur. */
function LeaveCancelForm({
  target,
  current,
  onClose,
  register,
}: {
  target: RequestFormTarget
  current: LeaveValues
  onClose: () => void
  register?: RegisterGuard
}) {
  const firm = useFirmChoice()
  const request = useDescriptionRequest({
    onClose,
    register,
    onError: firm.handle,
    build: (description, direct) => ({
      ...firm.body,
      kind: 'izin_iptal',
      sicilno: target.sicilno ?? '',
      leave_id: target.leaveId ?? null,
      description,
      direct,
    }),
  })
  const { editor, description, canApprove, direct, approving } = request
  return (
    <>
      <SheetHeader>
        <SheetTitle>{r.cancelLeave.title}</SheetTitle>
        <SheetDescription>{r.cancelLeave.targetsNote}</SheetDescription>
      </SheetHeader>
      <SheetBody className="flex flex-col gap-4">
        <span className="flex items-center gap-2 text-sm">
          <Avatar name={target.name ?? '?'} size="sm" />
          {target.name} · {target.sicilno}
        </span>
        <p className="text-sm">
          <span className="text-text-muted">{r.leave.current}: </span>
          {current.tur} · {formatLeaveRange(current)}
        </p>
        <DescriptionField
          id="hr-leave-cancel-description"
          placeholder={r.cancelLeave.descriptionPlaceholder}
          value={description}
          error={request.error}
          onChange={request.setDescription}
        />
        {firm.field}
        {canApprove && (
          <DirectToggle direct={direct} onChange={request.setDirect} hint={r.leave.directHint} />
        )}
      </SheetBody>
      <SheetFooter>
        <Button variant="secondary" onClick={() => editor.requestClose(onClose)}>
          {t.actions.cancel}
        </Button>
        <Button
          variant="danger"
          disabled={!firm.ready || editor.saving}
          onClick={() => void request.primary()}
        >
          {approving ? r.cancelLeave.submitDirect : r.submit}
        </Button>
      </SheetFooter>
      <SubmitConfirm
        open={request.confirming}
        title={r.directConfirmTitle}
        body={r.leave.approveConfirmBody}
        lines={[
          [r.person, `${target.name ?? ''} · ${target.sicilno ?? ''}`],
          [r.leave.current, `${current.tur} · ${formatLeaveRange(current)}`],
          [r.leave.requested, r.cancelLeave.requested],
          [r.descriptionLabel, description.trim()],
        ]}
        pending={editor.saving}
        confirmLabel={r.cancelLeave.submitDirect}
        onOpenChange={request.setConfirming}
        onConfirm={() => void editor.save().finally(() => request.setConfirming(false))}
      />
      {editor.dialog}
    </>
  )
}

function MovementRequestForm({
  target,
  onClose,
  register,
}: {
  target: RequestFormTarget
  onClose: () => void
  register?: RegisterGuard
}) {
  const [person, setPerson] = useState(
    target.sicilno ? { sicilno: target.sicilno, name: target.name ?? null } : null,
  )
  const form = useForm<MovementValue>({
    resolver: zodResolver(movementFormSchema),
    defaultValues: initialValue(target),
  })
  const value = useWatch({ control: form.control }) as MovementValue
  const edit = target.kind === 'giris_cikis_duzenle'
  // Onay izni olan, talebi onaya göndermeden doğrudan işleyebilir.
  const canApprove = useAuthStore((state) => state.hasPermission('hr.request:approve'))
  const [direct, setDirect] = useState(canApprove)
  const [confirming, setConfirming] = useState(false)

  const firm = useFirmChoice()
  const created = useRequestCreated(onClose)
  const check = useMovementCheck(person?.sicilno, value, target.hareketId)
  const blocked = (check?.errors.length ?? 0) > 0
  const time = normalizeTime(value.time)
  const body = (values: MovementValue): HrRequestCreate => ({
    ...firm.body,
    kind: target.kind,
    sicilno: person?.sicilno ?? '',
    giris_cikis: values.girisCikis,
    occurred_at: istanbulToIso(values.date, normalizeTime(values.time)),
    description: values.description.trim(),
    hareket_id: target.hareketId ?? null,
    direct: canApprove && direct,
  })
  const editor = useRecordEditor({
    form,
    submit: (values) => hrApi.createRequest(body(values)),
    onSaved: created,
    onError: firm.handle,
    serverFields: MOVEMENT_SERVER_FIELDS,
    failedTitle: r.actionFailed,
  })
  useRegisterGuard(register, editor.requestClose)
  const primary = async () => {
    if (!(canApprove && direct)) return editor.save()
    if (await form.trigger()) setConfirming(true)
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>{edit ? r.editTitle : r.newTitle}</SheetTitle>
        <SheetDescription>{r.targetsNote}</SheetDescription>
      </SheetHeader>
      <SheetBody className="flex flex-col gap-4">
        {edit ? (
          <span className="flex items-center gap-2 text-sm">
            <Avatar name={target.name ?? '?'} size="sm" />
            {target.name} · {target.sicilno}
          </span>
        ) : (
          <PersonPicker
            selected={person}
            onSelect={(next) => {
              setPerson(next)
              firm.reset()
            }}
          />
        )}
        <MovementFields
          idPrefix="hr-request"
          value={value}
          check={check}
          errors={fieldMessages(form.formState.errors)}
          onChange={(next) => assignValues(form, next)}
        />
        {firm.field}
        {canApprove && <DirectToggle direct={direct} onChange={setDirect} hint={r.directHint} />}
      </SheetBody>
      <SheetFooter>
        <Button variant="secondary" onClick={() => editor.requestClose(onClose)}>
          {t.actions.cancel}
        </Button>
        <Button
          disabled={!person || blocked || !firm.ready || editor.saving}
          onClick={() => void primary()}
        >
          {canApprove && direct ? r.submitDirect : r.submit}
        </Button>
      </SheetFooter>
      <SubmitConfirm
        open={confirming}
        title={r.directConfirmTitle}
        body={r.approveConfirmBody}
        lines={[
          [r.person, `${person?.name ?? ''} · ${person?.sicilno ?? ''}`],
          [r.type, t.girisCikis[value.girisCikis]],
          [r.columns.zaman, `${formatDateOnly(value.date)} ${time}`],
          [r.descriptionLabel, value.description.trim()],
        ]}
        pending={editor.saving}
        confirmLabel={r.submitDirect}
        onOpenChange={setConfirming}
        onConfirm={() => void editor.save().finally(() => setConfirming(false))}
      />
      {editor.dialog}
    </>
  )
}

/** Panelden eklenen hareketi DİA'dan silme talebi: hareketin kendisi gösterilir, açıklama sorulur. */
function MovementDeleteForm({
  target,
  onClose,
  register,
}: {
  target: RequestFormTarget
  onClose: () => void
  register?: RegisterGuard
}) {
  const request = useDescriptionRequest({
    onClose,
    register,
    build: (description, direct) => ({
      kind: 'giris_cikis_sil',
      sicilno: target.sicilno ?? '',
      hareket_id: target.hareketId ?? null,
      description,
      direct,
    }),
  })
  const { editor, description, canApprove, direct, approving } = request
  const d = r.deleteMovement
  const clock = cubeWallClock(target.wallClock)
  const movement = [
    target.girisCikis ? t.girisCikis[target.girisCikis] : '',
    clock ? `${formatDateOnly(clock.date)} ${clock.time}` : '',
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <>
      <SheetHeader>
        <SheetTitle>{d.title}</SheetTitle>
        <SheetDescription>{d.targetsNote}</SheetDescription>
      </SheetHeader>
      <SheetBody className="flex flex-col gap-4">
        <span className="flex items-center gap-2 text-sm">
          <Avatar name={target.name ?? '?'} size="sm" />
          {target.name} · {target.sicilno}
        </span>
        <p className="text-sm">
          <span className="text-text-muted">{d.movement}: </span>
          {movement}
        </p>
        <DescriptionField
          id="hr-movement-delete-description"
          placeholder={d.descriptionPlaceholder}
          value={description}
          error={request.error}
          onChange={request.setDescription}
        />
        {canApprove && (
          <DirectToggle direct={direct} onChange={request.setDirect} hint={r.directHint} />
        )}
      </SheetBody>
      <SheetFooter>
        <Button variant="secondary" onClick={() => editor.requestClose(onClose)}>
          {t.actions.cancel}
        </Button>
        <Button variant="danger" disabled={editor.saving} onClick={() => void request.primary()}>
          {approving ? d.submitDirect : r.submit}
        </Button>
      </SheetFooter>
      <SubmitConfirm
        open={request.confirming}
        title={r.directConfirmTitle}
        body={d.targetsNote}
        lines={[
          [r.person, `${target.name ?? ''} · ${target.sicilno ?? ''}`],
          [d.movement, movement],
          [r.descriptionLabel, description.trim()],
        ]}
        pending={editor.saving}
        confirmLabel={d.submitDirect}
        onOpenChange={request.setConfirming}
        onConfirm={() => void editor.save().finally(() => request.setConfirming(false))}
      />
      {editor.dialog}
    </>
  )
}
